import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { X, Send, Mic, MicOff, Volume2, VolumeX, Maximize2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { getVisitorFingerprint } from "../lib/fingerprint";
import { toast } from "sonner";
import SiriMode from "./SiriMode";
import SofiaChatChart from "./SofiaChatChart";

const SESSION_KEY = "sofia_session_id";

const GREETINGS = {
  en: "I'm Sofia — your concierge at Sofia Sands Realty. Tell me what you're after and I'll shortlist residences that fit. Budget, timeline, Dubai or Ras Al Khaimah?",
  ar: "أنا صوفيا — مستشارتك في صوفيا ساندز ريالتي. أخبرني بما تبحث عنه وسأرشح لك أفضل العقارات. الميزانية، الجدول الزمني، دبي أم رأس الخيمة؟",
  ru: "Я София — ваш консьерж в Sofia Sands Realty. Расскажите, что вы ищете, и я подберу подходящую недвижимость. Бюджет, сроки, Дубай или Рас-эль-Хайма?",
};

const QUICK_PROMPTS_MAP = {
  en: [
    "Find my residence in 60 seconds",
    "Top yields in Dubai under 2 million Dirhams",
    "Golden Visa pathway",
    "Off-plan vs ready — what's better?",
  ],
  ar: [
    "ابحث عن عقاري في 60 ثانية",
    "أعلى العوائد في دبي تحت 2 مليون درهم",
    "مسار التأشيرة الذهبية",
    "على الخارطة أم جاهز — أيهما أفضل؟",
  ],
  ru: [
    "Найти недвижимость за 60 секунд",
    "Лучшая доходность в Дубае до 2 млн дирхам",
    "Путь к золотой визе",
    "Строящееся или готовое — что лучше?",
  ],
};

function newSessionId() {
  // Use crypto.randomUUID for cryptographically secure session IDs
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return "sess_" + crypto.randomUUID();
  }
  // Fallback for older browsers
  return "sess_" + ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
}

// Gold S monogram in Cormorant Garamond
function SMonogram({ size = 22 }) {
  return (
    <span
      className="font-display italic leading-none text-gold"
      style={{ fontSize: size, fontWeight: 500, letterSpacing: "-0.04em" }}
      aria-hidden="true"
    >
      S
    </span>
  );
}

// Lightweight inline renderer for assistant replies: supports **bold** and *italic*.
// Premium surfaces should never show literal markdown characters.
function renderMarkdown(text) {
  if (!text) return null;
  const parts = [];
  const regex = /(\*\*([^*]+)\*\*|\*([^*]+)\*)/g;
  let lastIndex = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[2] !== undefined) {
      parts.push(<strong key={`b${key++}`} className="font-medium text-gold">{match[2]}</strong>);
    } else if (match[3] !== undefined) {
      parts.push(<em key={`i${key++}`} className="italic">{match[3]}</em>);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

export default function SofiaChat() {
  const { pathname } = useLocation();

  const [open, setOpen] = useState(false);
  const [chatMode, setChatMode] = useState(null); // null = show selector, "talk" = voice mode, "text" = text mode
  const [siriMode, setSiriMode] = useState(false); // fullscreen Siri-like voice interface
  const [sessionId] = useState(() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = newSessionId();
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  });
  const [messages, setMessages] = useState(() => [
    { role: "assistant", content: GREETINGS[localStorage.getItem("sofia_lang") || "en"] },
  ]);
  const [userMemory, setUserMemory] = useState({});
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({ name: "", email: "", whatsapp: "" });
  const [lang, setLang] = useState(() => localStorage.getItem("sofia_lang") || "en");

  const switchLang = (l) => {
    localStorage.setItem("sofia_lang", l);
    setLang(l);
  };
  const [recording, setRecording] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [speaking, setSpeaking] = useState(false);
  const recogRef = useRef(null);
  const audioRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  // Load chat history + memory for returning visitors
  useEffect(() => {
    if (historyLoaded) return;
    setHistoryLoaded(true);
    api.get(`/sofia/history/${sessionId}`)
      .then(({ data }) => {
        if (!data.returning || !data.messages?.length) return;
        const prior = data.messages.filter(m => m.role === "user" || m.role === "assistant");
        if (!prior.length) return;
        // Build welcome-back greeting
        const name = data.memory?.name;
        const welcomeMap = {
          en: name ? `Welcome back, ${name}. Picking up where we left off.` : "Welcome back. Picking up where we left off.",
          ar: name ? `مرحباً بعودتك، ${name}. نكمل من حيث توقفنا.` : "مرحباً بعودتك. نكمل من حيث توقفنا.",
          ru: name ? `С возвращением, ${name}. Продолжим с того места.` : "С возвращением. Продолжим с того места.",
        };
        const welcomeBack = welcomeMap[localStorage.getItem("sofia_lang") || "en"];
        setMessages([
          { role: "assistant", content: welcomeBack },
          ...prior,
        ]);
        setUserMemory(data.memory || {});
        // Auto-select text mode so the history is immediately visible
        setChatMode("text");
      })
      .catch(() => {}); // Silently ignore — new sessions just get the default greeting
  }, [sessionId, historyLoaded]);

  // Check for property context when chat opens
  useEffect(() => {
    if (open && chatMode === "text") {
      try {
        const ctx = localStorage.getItem("sofia_property_context");
        if (ctx) {
          const prop = JSON.parse(ctx);
          localStorage.removeItem("sofia_property_context");
          const contextMsg = `I'm interested in ${prop.title} (${prop.location}) - ${prop.bedrooms} bed, ${(prop.sqft || 0).toLocaleString()} sqft, AED ${(prop.price || 0).toLocaleString()}. Can you tell me more?`;
          setInput(contextMsg);
        }
      } catch {}
    }
  }, [open, chatMode]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }]);
    setLoading(true);
    try {
      const { data } = await api.post("/sofia/chat", {
        session_id: sessionId,
        message: content,
        lang,
      });
      setMessages((m) => {
        const next = [...m, { role: "assistant", content: data.reply, chart_data: data.chart_data || null }];
        // Surface the lead form once the conversation is a few turns in.
        if (next.length >= 4 && !showLeadForm) setShowLeadForm(true);
        return next;
      });
      // Speak Sofia's reply if voice is enabled (auto-listen in Talk mode)
      if (voiceEnabled && data.reply) {
        speakText(data.reply, chatMode === "talk");
      }
    } catch (e) {
      toast.error("Sofia is temporarily unavailable. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      toast.error("Voice input not supported in this browser.");
      return;
    }
    const r = new SR();
    r.lang = lang === "ar" ? "ar-AE" : lang === "ru" ? "ru-RU" : "en-US";
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setInput(transcript);
      // In Talk mode, auto-send after speech recognition completes
      if (chatMode === "talk" && transcript.trim()) {
        setTimeout(() => send(transcript), 100);
      }
    };
    r.onend = () => setRecording(false);
    r.onerror = (e) => {
      setRecording(false);
      if (e.error !== 'no-speech') {
        console.warn("Speech recognition error:", e.error);
      }
    };
    recogRef.current = r;
    r.start();
    setRecording(true);
  };
  const stopVoice = () => {
    recogRef.current?.stop();
    setRecording(false);
  };

  const speakText = async (text, autoListen = false) => {
    if (!voiceEnabled || !text) return;
    try {
      setSpeaking(true);
      const { data } = await api.post("/sofia/voice", { text: text.slice(0, 500) });
      if (data.audio) {
        const audioBlob = new Blob(
          [Uint8Array.from(atob(data.audio), c => c.charCodeAt(0))],
          { type: 'audio/mpeg' }
        );
        const audioUrl = URL.createObjectURL(audioBlob);
        if (audioRef.current) {
          audioRef.current.pause();
          URL.revokeObjectURL(audioRef.current.src);
        }
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        audio.onended = () => {
          setSpeaking(false);
          // Auto-start listening after Sofia finishes speaking in Talk mode
          if (autoListen && chatMode === "talk") {
            setTimeout(() => startVoice(), 300);
          }
        };
        audio.onerror = () => setSpeaking(false);
        await audio.play();
      } else {
        setSpeaking(false);
      }
    } catch (e) {
      console.warn("Voice playback failed:", e);
      setSpeaking(false);
    }
  };

  const stopSpeaking = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setSpeaking(false);
    }
  };

  const submitLead = async () => {
    if (!leadData.name) return toast.error("Please share your name.");
    if (!leadData.email && !leadData.whatsapp)
      return toast.error("Email or WhatsApp required.");
    try {
      const visitor_fingerprint = await getVisitorFingerprint();
      await api.post("/leads", {
        ...leadData,
        source: "sofia_chat",
        sofia_transcript: messages,
        visitor_fingerprint,
      });
      toast.success("Thank you — Sofia will follow up shortly.");
      setShowLeadForm(false);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content: `Perfect, ${leadData.name}. I've noted your details and our broker will reach out. Meanwhile — anything else I can research for you?`,
        },
      ]);
    } catch {
      toast.error("Could not save your details.");
    }
  };

  return (
    <>
      {/* Don't render the concierge on /crm (admin cockpit) */}
      {pathname.startsWith("/crm") ? null : (
        <>
      {/* FAB — Ink pill with gold S monogram, gold hairline, pulse ring */}
      {!open && (
        <motion.button
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 sm:right-[215px] z-[9999] group"
          data-testid="sofia-chat-fab"
          aria-label="Open Sofia chat"
        >
          <span className="relative inline-flex items-center gap-3 pl-3 pr-5 py-2.5 bg-ink text-bone rounded-full shadow-float overflow-visible">
            <span className="sofia-pulse relative w-11 h-11 rounded-full bg-ink border border-gold/60 flex items-center justify-center group-hover:bg-ink-light transition-colors">
              <SMonogram size={24} />
            </span>
            <span className="flex flex-col text-left pr-1">
              <span className="text-[0.62rem] tracking-[0.18em] uppercase text-bone/55 font-medium leading-none">
                Concierge
              </span>
              <span className="font-display text-[1.05rem] leading-none mt-1 text-bone group-hover:text-gold transition-colors">
                Ask Sofia
              </span>
            </span>
          </span>
        </motion.button>
      )}

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 60 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="fixed z-[9999] inset-0 sm:left-auto sm:w-[440px] h-[100dvh] bg-ink text-bone flex flex-col shadow-float border-l border-gold/40 overflow-hidden"
            data-testid="sofia-chat-panel"
            role="dialog"
            aria-label="Sofia concierge chat"
          >
            {/* Header */}
            <div className="px-6 py-5 flex items-center justify-between border-b border-bone/10">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-ink-light border border-gold/60 flex items-center justify-center overflow-hidden shadow-hairline">
                  <img
                    src="/sofia-logo.webp"
                    alt="Sofia Sands Realty"
                    className="w-10 h-10 object-contain"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                </div>
                <div>
                  <div className="font-display text-[1.15rem] leading-none text-bone">Sofia</div>
                  <div className="text-[0.65rem] tracking-caps uppercase text-gold/80 mt-1.5 font-medium flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${speaking ? "bg-gold animate-bounce" : "bg-gold animate-pulse"}`} />
                    {speaking ? "Speaking..." : "Online"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Language switcher */}
                <div className="flex items-center border border-bone/15 divide-x divide-bone/15">
                  {[
                    { code: "en", label: "EN" },
                    { code: "ar", label: "AR" },
                    { code: "ru", label: "RU" },
                  ].map(({ code, label }) => (
                    <button
                      key={code}
                      onClick={() => switchLang(code)}
                      className={`px-2 py-1 text-[0.6rem] font-medium tracking-widest transition-colors ${
                        lang === code ? "bg-gold text-ink" : "text-bone/40 hover:text-bone"
                      }`}
                      aria-label={`Switch to ${label}`}
                      title={`Switch to ${label}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => {
                    if (speaking) stopSpeaking();
                    setVoiceEnabled(!voiceEnabled);
                  }}
                  className={`p-1.5 transition-colors ${voiceEnabled ? "text-gold" : "text-bone/40 hover:text-bone/60"}`}
                  data-testid="sofia-voice-toggle"
                  aria-label={voiceEnabled ? "Disable voice" : "Enable voice"}
                  title={voiceEnabled ? "Voice enabled" : "Voice disabled"}
                >
                  {voiceEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="text-bone/60 hover:text-gold transition-colors"
                  data-testid="sofia-chat-close"
                  aria-label="Close chat"
                >
                  <X size={22} />
                </button>
              </div>
            </div>

            {/* Mode Selector - shown before conversation starts */}
            {chatMode === null && (
              <div className="flex-1 flex flex-col items-center justify-center px-8 py-10" data-testid="sofia-mode-selector">
                <div className="text-center mb-10">
                  <h3 className="font-display text-2xl text-bone mb-3">How would you like to connect?</h3>
                  <p className="text-bone/60 text-lg">Choose your preferred way to speak with Sofia</p>
                </div>
                <div className="flex flex-col gap-4 w-full max-w-sm">
                  <button
                    onClick={() => {
                      setSiriMode(true);
                      setOpen(false);
                    }}
                    className="flex items-center justify-center gap-3 p-6 bg-gold/20 border-2 border-gold hover:bg-gold/30 hover:border-gold transition-all group"
                    data-testid="sofia-mode-siri"
                  >
                    <Maximize2 size={24} className="text-gold" />
                    <div className="text-left">
                      <div className="font-display text-xl text-gold">Siri Mode</div>
                      <div className="text-bone/60 text-sm">Fullscreen voice experience</div>
                    </div>
                  </button>

                  <div className="flex gap-4">
                    <button
                      onClick={() => {
                        setChatMode("talk");
                        setVoiceEnabled(true);
                        setTimeout(() => speakText(GREETINGS[lang], true), 500);
                      }}
                      className="flex-1 flex flex-col items-center gap-4 p-8 bg-ink-light border-2 border-gold/40 hover:border-gold hover:bg-ink-light/80 transition-all group"
                      data-testid="sofia-mode-talk"
                  >
                    <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center group-hover:bg-gold/30 transition-colors">
                      <Mic size={32} className="text-gold" />
                    </div>
                    <span className="font-display text-xl text-bone group-hover:text-gold transition-colors">Talk</span>
                    <span className="text-bone/50 text-sm">Voice conversation</span>
                  </button>
                  <button
                    onClick={() => {
                      setChatMode("text");
                      setVoiceEnabled(false);
                    }}
                    className="flex-1 flex flex-col items-center gap-4 p-8 bg-ink-light border-2 border-bone/20 hover:border-gold hover:bg-ink-light/80 transition-all group"
                    data-testid="sofia-mode-text"
                  >
                    <div className="w-16 h-16 rounded-full bg-bone/10 flex items-center justify-center group-hover:bg-gold/20 transition-colors">
                      <Send size={32} className="text-bone/60 group-hover:text-gold transition-colors" />
                    </div>
                    <span className="font-display text-xl text-bone group-hover:text-gold transition-colors">Text</span>
                    <span className="text-bone/50 text-sm">Type your message</span>
                  </button>
                  </div>
                </div>
              </div>
            )}

            {/* Messages - shown after mode is selected */}
            {chatMode !== null && (
            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5 chat-scroll" data-testid="sofia-messages" dir={lang === "ar" ? "rtl" : "ltr"}>
              {messages.map((m, i) => (
                <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div
                    onClick={() => m.role === "assistant" && voiceEnabled && speakText(m.content)}
                    className={`max-w-[86%] px-5 py-4 text-base leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gold text-ink font-medium"
                        : "bg-ink-light text-bone border border-bone/5 cursor-pointer hover:border-gold/30 transition-colors"
                    }`}
                    title={m.role === "assistant" ? "Click to hear Sofia" : undefined}
                  >
                    {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
                  </div>
                  {m.chart_data && (
                    <div className="max-w-[86%] w-full">
                      <SofiaChatChart chartData={m.chart_data} />
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-ink-light px-4 py-3 border border-bone/5 text-bone/60 text-sm">
                    <span className="inline-flex gap-1">
                      <span className="w-1.5 h-1.5 bg-gold/60 rounded-full animate-pulse" />
                      <span className="w-1.5 h-1.5 bg-gold/60 rounded-full animate-pulse" style={{ animationDelay: "0.15s" }} />
                      <span className="w-1.5 h-1.5 bg-gold/60 rounded-full animate-pulse" style={{ animationDelay: "0.3s" }} />
                    </span>
                  </div>
                </div>
              )}
              <div ref={endRef} />
            </div>
            )}

            {/* Lead form - only show when chat mode is selected */}
            {chatMode !== null && showLeadForm && (
              <div className="border-t border-bone/10 bg-ink-light/50 px-6 py-5" data-testid="sofia-lead-form">
                <div className="overline overline-gold mb-3">Share your details for a tailored shortlist</div>
                <div className="space-y-3">
                  <input
                    placeholder="Name"
                    value={leadData.name}
                    onChange={(e) => setLeadData({ ...leadData, name: e.target.value })}
                    className="w-full bg-transparent border-b border-bone/20 focus:border-gold py-2 text-sm text-bone placeholder:text-bone/40 outline-none font-light"
                    data-testid="lead-name-input"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      placeholder="Email"
                      value={leadData.email}
                      onChange={(e) => setLeadData({ ...leadData, email: e.target.value })}
                      className="bg-transparent border-b border-bone/20 focus:border-gold py-2 text-sm text-bone placeholder:text-bone/40 outline-none font-light"
                      data-testid="lead-email-input"
                    />
                    <input
                      placeholder="WhatsApp"
                      value={leadData.whatsapp}
                      onChange={(e) => setLeadData({ ...leadData, whatsapp: e.target.value })}
                      className="bg-transparent border-b border-bone/20 focus:border-gold py-2 text-sm text-bone placeholder:text-bone/40 outline-none font-light"
                      data-testid="lead-whatsapp-input"
                    />
                  </div>
                </div>
                <button
                  onClick={submitLead}
                  className="mt-4 w-full bg-gold text-ink hover:bg-bone py-3 text-[0.72rem] tracking-caps uppercase font-medium transition-colors"
                  data-testid="lead-submit-button"
                >
                  Send to Sofia
                </button>
              </div>
            )}

            {/* Quick prompts (only on first turn, after mode selected) */}
            {chatMode !== null && messages.length < 2 && (
              <div className={`px-6 py-4 flex flex-wrap gap-3 border-t border-bone/10 ${lang === "ar" ? "flex-row-reverse" : ""}`} dir={lang === "ar" ? "rtl" : "ltr"}>
                {(QUICK_PROMPTS_MAP[lang] || QUICK_PROMPTS_MAP.en).map((q) => (
                  <button
                    key={q}
                    onClick={() => send(q)}
                    className="text-sm px-4 py-2.5 border border-bone/15 hover:border-gold hover:text-gold text-bone/80 transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input row - only show when chat mode is selected */}
            {chatMode !== null && (
            <div className="border-t border-bone/10 p-5 flex items-center gap-3">
              <button
                onClick={recording ? stopVoice : startVoice}
                className={`p-3 border transition-colors ${recording ? "border-gold text-gold bg-gold/10" : "border-bone/15 text-bone/60 hover:border-gold hover:text-gold"}`}
                data-testid="sofia-voice-button"
                aria-label="Voice input"
              >
                {recording ? <MicOff size={20} /> : <Mic size={20} />}
              </button>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask Sofia anything…"
                className="flex-1 px-4 py-3 text-base bg-ink-light text-bone placeholder:text-bone/40 border border-bone/10 focus:border-gold outline-none"
                data-testid="sofia-input"
              />
              <button
                onClick={() => send()}
                disabled={loading}
                className="bg-gold text-ink hover:bg-bone p-3 disabled:opacity-50 transition-colors"
                data-testid="sofia-send-button"
                aria-label="Send"
              >
                <Send size={20} />
              </button>
            </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
        </>
      )}

      {/* Siri Mode - Fullscreen Voice Interface */}
      <AnimatePresence>
        {siriMode && (
          <SiriMode
            sessionId={sessionId}
            onClose={() => {
              setSiriMode(false);
              setOpen(true);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
